import { ChatAttachment, SelectableContext } from '../types'
import { getWordCount } from '../utils/stringUtils'
import { useCallback } from 'react'
import { useCurrentWindowToSelection, useIsCurrentWindowAttached, useSelectedWindows } from '../store/atoms'
import { ExplicitWindowContextScope } from '@highlight/common'
import { useWebviewContext } from '../store/webview'

export const useContextToAttachments = () => {
  const selectedWindows = useSelectedWindows()
  const currentWindow = useCurrentWindowToSelection()
  const isCurrentWindowAttached = useIsCurrentWindowAttached()
  const webviewContext = useWebviewContext()

  return useCallback(() => {
    const windows = [...(isCurrentWindowAttached ? [currentWindow] : []), ...selectedWindows]
    const attachments: ChatAttachment[] = []

    for (const window of windows) {
      const context = window?.context
      if (!context) {
        continue
      }
      attachments.push(...selectableContextToAttachments(context, window.scope))
    }

    // add webview context if it exists
    if (webviewContext) {
      attachments.push({
        attachment: {
          type: 'windowContents',
          text: webviewContext.text,
          name: webviewContext.title,
          appName: 'Web Browser',
          appIcon: webviewContext.favicon,
          url: webviewContext.url,
          words: getWordCount(webviewContext.text),
          createdAt: new Date().toISOString(),
        },
      })

      if (webviewContext.screenshot) {
        attachments.push({
          pending: {
            type: 'image',
            name: webviewContext.title,
            body: webviewContext.screenshot,
          },
        })
      }
    }

    return attachments
  }, [selectedWindows, currentWindow, isCurrentWindowAttached, webviewContext])
}

export const selectableContextToAttachments = (
  windowContext: SelectableContext,
  scope?: ExplicitWindowContextScope,
) => {
  const attachments: ChatAttachment[] = []

  // Check if selected text was detected
  if (windowContext.selectedText?.length) {
    attachments.push({
      attachment: {
        type: 'selectedText',
        text: windowContext.selectedText,
      },
    })
  }

  // Check if auto-detected screenshot was detected
  if (windowContext.autodetectedClipboardImage) {
    attachments.push({
      pending: {
        type: 'image',
        body: windowContext.autodetectedClipboardImage,
        name: 'Screenshot',
      },
    })
  }

  const fromBrowserAPI = windowContext.fullTextSource === 'browserAPI'
  const isFullScope = windowContext.fullText && scope === 'full'
  const isBrowserAPIScope = scope === 'browserAPI'

  // For visible scope, attach the screenshot of the window context
  const screenshot = windowContext.displayScreenshot
  if (screenshot && (isFullScope || !fromBrowserAPI)) {
    attachments.push({
      pending: {
        type: 'image',
        name: windowContext.windowTitle ?? windowContext.appName,
        body: screenshot,
        icon: isFullScope ? windowContext.appIcon : undefined, // only attach for previews for full scope
      },
    })
  }

  // If visible text exists, always attach it as "OCR text"
  const visibleOcrText = windowContext.visibleText
  if (visibleOcrText) {
    attachments.push({
      attachment: {
        type: 'ocr',
        text: visibleOcrText,
      },
    })
  }

  // If window scope was explicitly (or automatically via browser extension)attached,
  // include it as a windowContents attachment
  if (isFullScope || fromBrowserAPI || isBrowserAPIScope) {
    attachments.push({
      attachment: {
        type: 'windowContents',
        text: windowContext.fullText!,
        name: windowContext.windowTitle ?? 'Window', // @TODO enforce strict types -- we should always have a window title?
        appName: windowContext.appName,
        appIcon: windowContext.appIcon,
        url: windowContext.url,
        words: getWordCount(windowContext.fullText!),
        createdAt: new Date().toISOString(),
      },
    })
  }

  return attachments
}
